import Button from '@/components/Button'
import DateDisplay from '@/components/DateDisplay'
import Layout, { Stack } from '@/components/Layout'
import Text from '@/components/Text'
import { ContextInterface as BookingContextInterface } from '@/context/booking'
import { lobbyUrl } from '@/lib/lobbyUrl'
import { JoinBookedSessionCardAction, SessionCard } from '@/shared/api-generated-types'
import { useTranslation } from 'next-i18next'
import { useEffect, useState } from 'react'
import { CardPrimaryAction } from '../ClassCard'

export interface Props {
  session: Partial<SessionCard>
  bookingState: BookingContextInterface['state']
  noCancel?: boolean
  multipleSession?: boolean
  onClickCancel?: () => void
  onClickViewAll?: () => void
  handleBookingAction: (session: Partial<SessionCard>) => void
}

const ClassDetailsCta = ({
  session,
  bookingState,
  noCancel,
  multipleSession,
  onClickCancel,
  onClickViewAll,
  handleBookingAction,
}: Props): JSX.Element => {
  const { primaryAction, sessionId, start } = session
  const { t } = useTranslation('classes')
  const [buttonLabel, setButtonLabel] = useState<string | undefined>(primaryAction?.label && t('slotCard.book'))
  const [isLobbyButton, setIsLobbyButton] = useState<boolean>(
    primaryAction?.__typename === 'JoinBookedSessionCardAction' ||
      primaryAction?.__typename === 'JoinLiveSessionCardAction',
  )
  const [isBooking, setIsBooking] = useState<boolean>(false)
  const [isBooked, setIsBooked] = useState<boolean>(false)
  const [bookingFailed, setBookingFailed] = useState<boolean>(false)

  const isDisabled = isBooked || bookingFailed || isBooking

  const bookedPrimaryAction = {
    __typename: 'JoinBookedSessionCardAction',
    url: lobbyUrl(`/session/${sessionId}`),
    label: t('slotCard.enter'),
  } as JoinBookedSessionCardAction

  useEffect(() => {
    if (bookingState.fetching?.find((s) => s === sessionId)) {
      setButtonLabel(t('slotCard.booking'))
      setIsBooking(true)
    } else if (bookingState.booked?.find((s) => s === sessionId)) {
      setButtonLabel(t('slotCard.booked'))
      setIsBooked(true)
      setIsBooking(false)
    } else if (bookingState.failed?.find((s) => s === sessionId)) {
      setButtonLabel(t('slotCard.bookingFailed'))
      setBookingFailed(true)
      setIsBooking(false)
    }
  }, [bookingState])

  // After 1.5 seconds, lets show the next action for a user to take
  useEffect(() => {
    const timer = setTimeout(() => {
      if (isBooked) {
        setIsBooked(false)
        setButtonLabel(t('slotCard.enter'))
        setIsLobbyButton(true)
      }

      if (bookingFailed) {
        setBookingFailed(false)
        setButtonLabel(t('slotCard.book'))
      }
    }, 1500)
    return () => clearTimeout(timer)
  }, [isBooked, bookingFailed])

  return (
    <Layout>
      <Stack y>
        <Stack x alignItems="center" justifyContent={!multipleSession ? 'center' : 'space-between'}>
          {start && (
            <Text size="xxxs" weight="md" className="py-xxs">
              <DateDisplay value={start} simple />
            </Text>
          )}
          {multipleSession ? (
            <Button theme="clear" label={t('classDetailsCta.viewAllCta')} onClick={onClickViewAll} sharpCorners />
          ) : null}
        </Stack>
        {isLobbyButton ? (
          <Stack x>
            {!noCancel ? (
              <Button
                theme="secondaryOutline"
                label={t('classDetailsCta.cancelCta')}
                stretch
                onClick={onClickCancel}
                sharpCorners
              />
            ) : null}
            <CardPrimaryAction primaryAction={bookedPrimaryAction}>
              <Button size="md" theme="secondary" sharpCorners stretch label={bookedPrimaryAction.label} />
            </CardPrimaryAction>
          </Stack>
        ) : (
          <Stack x>
            <CardPrimaryAction primaryAction={primaryAction} makeBooking={() => handleBookingAction(session)}>
              <Button size="md" theme="primary" disabled={isDisabled} label={buttonLabel} sharpCorners stretch />
            </CardPrimaryAction>
          </Stack>
        )}
      </Stack>
    </Layout>
  )
}
export default ClassDetailsCta
